/**
 * Review Tab — AI review panel.
 *
 * Role-aware scope selector + thread picker + findings list + run flow.
 * Polls GET /review-runs/:id every 3s while a run is in-flight (spec
 * fallback for WebSocket updates).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ShieldCheck, ListChecks, AlertCircle, X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useReviewStore } from '@/stores/review-store'
import { useReviewPermissions } from '@/hooks/useReviewPermissions'
import { useDraftingStore } from '@/stores/drafting-store'
import { CriteriaDrawer } from '@/components/drafting/CriteriaDrawer'
import { FindingsList } from '@/components/drafting/FindingsList'
import { NewReviewForm } from '@/components/drafting/NewReviewForm'
import { useCommentsStore } from '@/stores/comments-store'
import { ReviewThreadPicker } from '@/components/drafting/ReviewThreadPicker'
import { ReviewRunNotice } from '@/components/drafting/ReviewRunNotice'
import { keepCriteriaForScope, type MergedReviewFinding } from '@/lib/schemas'
import { reviewRunNotice } from '@/lib/review-run-notice'
import { getRun } from '@/lib/review-api'
import { flashSection } from '@/lib/editor-bridge'
import { useAgentStore } from '@/stores/agent-store'
import { useUnifiedAiPanelStore } from '@/stores/unified-ai-panel-store'
import { DOCUMENT_SECTION_KEY } from '@/types/scope'

const POLL_MS = 3000

const SECTION_TITLE_MAX = 80

/**
 * Resolve a human-readable label for an outline section. Narrative-document
 * sections carry `title`; questionnaire items carry `prompt` (and an
 * optional `number`). Returns undefined when neither is available, so
 * callers can fall back to the raw section key.
 */
function resolveSectionTitle(section: unknown): string | undefined {
  if (!section || typeof section !== 'object') return undefined
  const s = section as { title?: string; prompt?: string; number?: string }
  if (s.title && s.title.trim().length > 0) return s.title.trim()
  if (s.prompt && s.prompt.trim().length > 0) {
    const prompt = s.prompt.trim()
    const label = s.number ? `${s.number} ${prompt}` : prompt
    return label.length > SECTION_TITLE_MAX
      ? `${label.slice(0, SECTION_TITLE_MAX - 1)}…`
      : label
  }
  return undefined
}

interface ReviewTabProps {
  bidId: string
  artifactId: string
  sectionKey: string
  scopeKind?: 'unit' | 'artifact'
}

export function ReviewTab({
  bidId,
  artifactId,
  sectionKey,
  scopeKind = 'unit',
}: ReviewTabProps) {
  const {
    scope,
    setScope,
    selectedCriteriaIds,
    clearSelectedCriteria,
    startReview,
    cancelReview,
    activeRunId,
    activeRunStatus,
    progress,
    error,
    startFix,
  } = useReviewStore()
  const loadActiveFindings = useReviewStore((s) => s.loadActiveFindings)
  const loadCriteria = useReviewStore((s) => s.loadCriteria)
  const activeFindings = useReviewStore((s) => s.activeFindings)
  const activeFindingsLoading = useReviewStore((s) => s.activeFindingsLoading)
  const handleWorkerUpdate = useReviewStore((s) => s.handleWorkerUpdate)
  const selectedThreadId = useReviewStore((s) => s.selectedThreadId)
  const runHistory = useReviewStore((s) => s.runHistory)
  const lastRun = useReviewStore((s) => s.lastRun)

  const permission = useReviewPermissions(
    bidId,
    artifactId,
    scopeKind === 'unit' ? sectionKey : null,
  )
  const outline = useDraftingStore((s) => s.outline)
  const sectionTitle = resolveSectionTitle(
    outline?.sections.find((s) => s.sectionKey === sectionKey),
  )
  // Single-document artifacts (scopeKind='artifact') have no outline by
  // design — the whole draft is one virtual section. Only block the Run
  // button for multi-section artifacts with an actually empty outline.
  const outlineHasSections =
    (outline?.sections.length ?? 0) > 0 || scopeKind === 'artifact'

  const sectionTitleFor = useCallback(
    (id: string | null) => {
      if (!id) return null
      // Artifact-scoped single-document virtual key.
      if (id === DOCUMENT_SECTION_KEY || id === '__DOCUMENT__') return 'Document'
      const section = outline?.sections.find((s) => s.sectionKey === id)
      return resolveSectionTitle(section) ?? id
    },
    [outline],
  )

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [showNewForm, setShowNewForm] = useState(false)
  const [scopeNotice, setScopeNotice] = useState<string | null>(null)
  const pollRef = useRef<number | null>(null)

  const allCriteria = useReviewStore((s) => s.criteria)

  /**
   * Switching scope can invalidate what is already ticked — whole-response
   * criteria mean nothing against one section, and the server rejects the
   * combination outright. Drop them and say so; never send an invalid set and
   * never clear silently.
   */
  const pruneSelectionForScope = useCallback(
    (next: 'artifact' | 'section') => {
      const known = [...(allCriteria?.system ?? []), ...(allCriteria?.mine ?? [])]
      const current = useReviewStore.getState().selectedCriteriaIds
      const kept = keepCriteriaForScope(current, known, next)
      const droppedCount = current.length - kept.length
      if (droppedCount === 0) return false
      useReviewStore.setState({ selectedCriteriaIds: kept })
      setScopeNotice(
        droppedCount === 1
          ? '1 whole-response check cleared — it needs the full document.'
          : `${droppedCount} whole-response checks cleared — they need the full document.`,
      )
      return true
    },
    [allCriteria],
  )

  const handleScopeChange = useCallback(
    (next: 'artifact' | 'section') => {
      if (!pruneSelectionForScope(next)) setScopeNotice(null)
      if (next === 'artifact') setScope('artifact')
      else setScope('section', sectionKey)
    },
    [pruneSelectionForScope, setScope, sectionKey],
  )

  // Initialize scope from panel context on mount / artifact change.
  //
  // This forces the scope WITHOUT the user touching the toggle — clicking a
  // different section in the editor lands here — so it must prune the selection
  // too. Otherwise a whole-response criterion ticked at artifact scope survives
  // the forced switch back to section scope, the picker then hides it (so there
  // is no checkbox left to untick), and Run Review returns 400 with no way out
  // but toggling scope back and forth.
  useEffect(() => {
    const next = scopeKind === 'artifact' ? 'artifact' : 'section'
    pruneSelectionForScope(next)
    if (next === 'artifact') setScope('artifact')
    else setScope('section', sectionKey)
  }, [scopeKind, sectionKey, setScope, pruneSelectionForScope])

  // Load criteria + active findings on mount / artifact change.
  useEffect(() => {
    if (!artifactId) return
    void loadCriteria()
    void loadActiveFindings(artifactId)
  }, [artifactId, loadCriteria, loadActiveFindings])

  // Polling while a run is in-flight (WebSocket fallback per spec).
  useEffect(() => {
    if (activeRunStatus !== 'running' || !activeRunId) return
    const poll = async () => {
      try {
        const run = await getRun(activeRunId)
        handleWorkerUpdate({ runId: run.id, status: run.status, errorMessage: run.errorMessage })
        if (run.status === 'complete') {
          // Refresh the merged active-findings view so the run's findings show up.
          await loadActiveFindings(artifactId)
          return
        }
        pollRef.current = window.setTimeout(poll, POLL_MS)
      } catch {
        pollRef.current = window.setTimeout(poll, POLL_MS)
      }
    }
    pollRef.current = window.setTimeout(poll, POLL_MS)
    return () => {
      if (pollRef.current !== null) window.clearTimeout(pollRef.current)
      pollRef.current = null
    }
  }, [activeRunId, activeRunStatus, artifactId, handleWorkerUpdate, loadActiveFindings])

  const selectedRunFindings = useMemo<MergedReviewFinding[] | null>(() => {
    if (selectedThreadId === 'active' || !activeFindings) return activeFindings
    const run = runHistory.find((r) => r.id === selectedThreadId)
    if (!run) return activeFindings
    return activeFindings.filter((f) => f.runId === selectedThreadId)
  }, [selectedThreadId, activeFindings, runHistory])

  // Scope label for the currently-displayed findings. 'active' (merged view)
  // spans runs of mixed scopes, so we show a neutral label. A specific run
  // has an explicit sectionKey (null = full document).
  const selectedScopeLabel = useMemo<string | null>(() => {
    if (selectedThreadId === 'active') return null
    const run = runHistory.find((r) => r.id === selectedThreadId)
    if (!run) return null
    if (run.sectionKey === null) return 'Full document'
    return sectionTitleFor(run.sectionKey) ?? 'This section'
  }, [selectedThreadId, runHistory, sectionTitleFor])

  const canFixSection = permission.canFixSection

  // Comment promotion bridge: reuses comments-store to create a promoted
  // comment carrying source_finding_ref, per spec §6.6.
  const promoteFromFinding = useCommentsStore((s) => s.promoteFromFinding)
  const loadCommentPermissions = useCommentsStore((s) => s.loadPermissions)
  const commentPermissions = useCommentsStore((s) => s.permissions)
  const promotedMap = useCommentsStore((s) => s.promotedFromReview)
  const promotedSet = useMemo(
    () => promotedMap[artifactId] ?? new Set<string>(),
    [promotedMap, artifactId],
  )

  // Kick off perm load for artifact-level (used to gate the promotion button
  // in the common case — reviewer permission already implies section perm).
  useEffect(() => {
    if (!bidId || !artifactId) return
    loadCommentPermissions(bidId, artifactId, null).catch(() => {})
  }, [bidId, artifactId, loadCommentPermissions])

  const artifactCommentPerms = commentPermissions[`${artifactId}:__artifact__`]
  const canPromoteToComment = artifactCommentPerms?.canPromoteFromReview ?? false

  const handlePromote = useCallback(
    async (finding: MergedReviewFinding) => {
      if (!finding.sectionId) return
      try {
        await promoteFromFinding(bidId, artifactId, {
          sectionId: finding.sectionId,
          startPos: null,
          endPos: null,
          quotedText: finding.excerpt ?? null,
          body: finding.message,
          sourceFindingRef: {
            issueKey: finding.issueKey,
            runId: finding.runId,
            findingIndex: finding.findingIndex,
            criterion: finding.criterionName,
            severity: finding.severity,
          },
        })
      } catch {
        // Non-fatal; leave the button available for retry.
      }
    },
    [bidId, artifactId, promoteFromFinding],
  )

  const handleFix = useCallback(async (finding: MergedReviewFinding) => {
    if (!finding.sectionId) return
    const response = await startFix(artifactId, finding.issueKey)
    if (!response) return

    // Hand off to the Ask AI (`write`) tab: pre-populate input, switch tab, then
    // switch to the new thread inside agent-store.
    useUnifiedAiPanelStore.getState().setPendingSeed(response.threadSeed, response.threadId)
    useUnifiedAiPanelStore.getState().setTab('write')
    try {
      await useAgentStore.getState().switchThread(response.threadId)
    } catch {
      // Non-fatal; the panel will still show the input seed.
    }
    flashSection(finding.sectionId, { excerpt: finding.excerpt ?? null })
  }, [artifactId, startFix])

  // Permission check: hide when the caller has no scope at all.
  if (
    permission.permissions &&
    !permission.canArtifactScope &&
    !permission.canSectionScope
  ) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-8 px-4 text-center">
        <div className="h-12 w-12 rounded-full flex items-center justify-center mb-3 bg-muted">
          <ShieldCheck className="h-5 w-5 text-muted-foreground" />
        </div>
        <h4 className="text-sm font-medium mb-1">Review unavailable</h4>
        <p className="text-meta max-w-[240px]">
          You need a section assignment or artifact-wide review access to run AI reviews here.
        </p>
      </div>
    )
  }

  const canToggleScope = permission.canArtifactScope
  const visibleScope = canToggleScope ? scope : 'section'

  const handleRunReview = async () => {
    if (selectedCriteriaIds.length === 0) return
    const runId = await startReview({
      bidId,
      artifactId,
      sectionKey: visibleScope === 'section' ? sectionKey : null,
      criteriaIds: selectedCriteriaIds,
    })
    if (runId) {
      setShowNewForm(false)
      setScopeNotice(null)
      clearSelectedCriteria()
    }
  }

  const canRun =
    selectedCriteriaIds.length > 0 &&
    outlineHasSections &&
    activeRunStatus !== 'running'

  const isRunning = activeRunStatus === 'running'
  const activeCount = activeFindings?.length ?? 0

  // Which run the "couldn't check" note belongs to. A picked run speaks for
  // itself; the merged view speaks for the run this session started here —
  // never one from another response, or a section that is not open.
  const ownRun = lastRun?.artifactId === artifactId ? lastRun : null
  const noticeRun =
    selectedThreadId === 'active'
      ? ownRun &&
        (ownRun.sectionKey === null || visibleScope === 'artifact' || ownRun.sectionKey === sectionKey)
        ? ownRun
        : null
      : ownRun?.runId === selectedThreadId
        ? ownRun
        : runHistory.find((r) => r.id === selectedThreadId) ?? null
  const runNotice = reviewRunNotice(noticeRun)

  return (
    <div className="flex flex-col h-full">
      {/* Thread picker strip */}
      <div className="shrink-0">
        <div className="flex items-center gap-2 px-3 py-1.5">
          <ReviewThreadPicker artifactId={artifactId} activeCount={activeCount} />
        </div>
      </div>

      {/* Run banner / error banner */}
      {error && (
        <div className="mx-3 mt-2 flex items-center gap-2 text-xs text-destructive bg-destructive/10 rounded-md px-2 py-1.5">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1 min-w-0 truncate">{error}</span>
          <button onClick={() => useReviewStore.setState({ error: null })} aria-label="Dismiss error">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {isRunning && activeRunId && (
        <div className="mx-3 mt-2 flex items-center gap-2 text-xs text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 rounded-md px-2 py-1.5">
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
          <span className="flex-1 min-w-0 truncate">
            Checking…{' '}
            {progress ? `${progress.current} of ${progress.total} criteria` : ''}
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-[11px]"
            onClick={() => { void cancelReview(activeRunId) }}
          >
            Cancel
          </Button>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-3 scrollbar-thin">
        {showNewForm ? (
          <NewReviewForm
            onCancel={() => {
              setShowNewForm(false)
              setScopeNotice(null)
            }}
            onRun={() => void handleRunReview()}
            canRun={canRun}
            outlineHasSections={outlineHasSections}
            onManageCriteria={() => setDrawerOpen(true)}
            selectedCount={selectedCriteriaIds.length}
            isRunning={isRunning}
            canToggleScope={canToggleScope}
            scope={visibleScope}
            sectionKey={sectionKey}
            sectionTitle={sectionTitle ?? null}
            scopeNotice={scopeNotice}
            onScopeChange={handleScopeChange}
          />
        ) : activeFindings === null || activeFindingsLoading ? (
          <div className="flex flex-col items-center justify-center py-10 text-center text-meta">
            <Loader2 className="h-5 w-5 animate-spin mb-2" />
            Loading checks…
          </div>
        ) : !runNotice && selectedRunFindings && selectedRunFindings.length === 0 ? (
          <EmptyStateNoFindings
            scopeLabel={visibleScope === 'artifact' ? 'full document' : sectionTitle ?? 'this section'}
            onNew={() => setShowNewForm(true)}
          />
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-meta">
                {selectedThreadId === 'active'
                  ? `Active issues · ${selectedRunFindings?.length ?? 0}`
                  : `Run findings · ${selectedRunFindings?.length ?? 0}${selectedScopeLabel ? ` · ${selectedScopeLabel}` : ''}`}
              </p>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowNewForm(true)}>
                + New check
              </Button>
            </div>
            {runNotice && <ReviewRunNotice message={runNotice} />}
            <FindingsList
              artifactId={artifactId}
              findings={selectedRunFindings ?? []}
              canFixSection={canFixSection}
              sectionTitleFor={sectionTitleFor}
              onFix={(finding) => { void handleFix(finding) }}
              canPromoteToComment={canPromoteToComment}
              onPromoteToComment={(finding) => { void handlePromote(finding) }}
              promotedKeys={promotedSet}
            />
          </div>
        )}
      </div>

      <CriteriaDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  )
}

function EmptyStateNoFindings({ scopeLabel, onNew }: { scopeLabel: string; onNew: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="h-10 w-10 rounded-full flex items-center justify-center mb-2 bg-muted">
        <ListChecks className="h-5 w-5 text-muted-foreground" />
      </div>
      <p className="text-meta max-w-[240px] mb-3">
        No active issues in {scopeLabel}.
      </p>
      <Button size="sm" variant="outline" onClick={onNew}>
        + New check
      </Button>
    </div>
  )
}
